const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg|ogv|mov|m4v)(?:[?#]|$)/i

/** Mux playback IDs from player / stream / image CDN URLs. */
const MUX_PLAYBACK_ID_RE =
  /(?:player\.mux\.com|stream\.mux\.com|image\.mux\.com)\/([A-Za-z0-9]+)/i

export function isVideoFileUrl(url: string | null | undefined): boolean {
  if (!url) return false
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url
    return VIDEO_FILE_PATTERN.test(path)
  } catch {
    return VIDEO_FILE_PATTERN.test(url)
  }
}

/** Extract a Mux playback ID from a raw URL string. */
export function getMuxPlaybackId(url?: string | null): string | null {
  if (!url?.trim()) return null
  const match = url.trim().match(MUX_PLAYBACK_ID_RE)
  if (match?.[1]) return match[1]

  try {
    const parsed = new URL(url.trim())
    if (!parsed.hostname.includes('mux.com')) return null
    return parsed.pathname.match(/^\/([^/?]+)/)?.[1] ?? null
  } catch {
    return null
  }
}

export function getMuxThumbnailUrl(playbackId: string): string {
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=1`
}

/** Append muted autoplay (+ loop) params required by browser / host policies. */
export function withEmbedAutoplay(embedUrl: string): string {
  try {
    const u = new URL(embedUrl)

    if (u.hostname.includes('youtube.com')) {
      u.searchParams.set('autoplay', '1')
      u.searchParams.set('mute', '1')
      u.searchParams.set('muted', '1')
      u.searchParams.set('playsinline', '1')
      u.searchParams.set('loop', '1')
      const id = u.pathname.split('/embed/')[1]?.split(/[/?#]/)[0]
      if (id && !u.searchParams.get('playlist')) {
        u.searchParams.set('playlist', id)
      }
      return u.toString()
    }

    if (u.hostname.includes('player.vimeo.com')) {
      u.searchParams.set('autoplay', '1')
      u.searchParams.set('muted', '1')
      u.searchParams.set('loop', '1')
      u.searchParams.set('background', '1')
      return u.toString()
    }

    return embedUrl
  } catch {
    return embedUrl
  }
}

/**
 * Convert YouTube / Vimeo page URLs to embeddable iframe src.
 * Returns null when the URL should play as a native `<video>` instead
 * (direct file / Vimeo progressive_redirect).
 */
export function toVideoEmbedUrl(
  raw: string,
  opts?: { autoplay?: boolean },
): string | null {
  const url = raw.trim()
  if (!url) return null

  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    const host = u.hostname.replace(/^www\./, '')
    const path = u.pathname

    if (
      isVideoFileUrl(url) ||
      path.includes('/progressive_redirect/') ||
      path.includes('/external/') ||
      /\.mp4(?:$|[?#])/i.test(path)
    ) {
      return null
    }

    let embed: string | null = null

    if (host.includes('youtube.com') && path.startsWith('/embed/')) {
      embed = `${u.origin}${path}`
    } else if (host === 'player.vimeo.com' && path.startsWith('/video/')) {
      embed = `${u.origin}${path}`
      const h = u.searchParams.get('h')
      if (h) embed += `?h=${encodeURIComponent(h)}`
    } else if (host === 'youtu.be') {
      const id = path.replace(/^\//, '').split('/')[0]
      embed = id ? `https://www.youtube.com/embed/${id}` : null
    } else if (host.includes('youtube.com')) {
      const id = u.searchParams.get('v')
      if (id) embed = `https://www.youtube.com/embed/${id}`
      const shorts = path.match(/\/shorts\/([^/]+)/)
      if (!embed && shorts?.[1]) {
        embed = `https://www.youtube.com/embed/${shorts[1]}`
      }
    } else if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
      const parts = path.split('/').filter(Boolean)
      if (parts[0] && /^\d+$/.test(parts[0])) {
        const id = parts[0]
        const privacyHash =
          parts[1] && !/^\d+$/.test(parts[1]) ? parts[1] : null
        embed = `https://player.vimeo.com/video/${id}`
        if (privacyHash) {
          embed += `?h=${encodeURIComponent(privacyHash)}`
        }
      }
    }

    if (!embed) return null
    return opts?.autoplay ? withEmbedAutoplay(embed) : embed
  } catch {
    return null
  }
}
